import { AccessoriesPageData } from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { UserSettings } from 'src/types/UserSettings.ts';
import { getPriceValue } from 'src/modules/checkout/getPriceValue.ts';
import { sortAccessoriesBy } from 'src/modules/common/utils/sortAccessories.ts';
import { ComboData } from 'src/modules/checkout/CheckoutPageMapping.ts';
import { ComboState, LocationState } from 'src/modules/LocationState.ts';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { Accessory, prepareAccessories } from 'src/common/prepareAccessories.ts';
import { subtotalAccessories } from 'src/common/subtotalAccessories.ts';

export const calculateTotals = (
    combo: ComboData,
    {
        valve,
        actuator,
        actuatorAccessories: actuatorAccessoriesSource,
        valveAccessories: valveAccessoriesSource,
    }: AccessoriesPageData['vssAccessories'],
    userSettings: UserSettings,
    checkoutState: Omit<LocationState, 'combo'> & { combo: ComboState },
    currency: string | null,
    isPipeConnectorEnabled: boolean,
): {
    currentCurrency: string;
    pricePerUnit: number;
    total: number;
    comboSubtotal: number;
    pipeConnectors: Accessory<AccessoriesPageData['vssAccessories']['valveAccessories']['0']>[];
    pipeConnectorsSubtotal: number;
    valveAccessories: Accessory<AccessoriesPageData['vssAccessories']['valveAccessories']['0']>[];
    valveAccessoriesSubtotal: number;
    actuatorAccessories: Accessory<AccessoriesPageData['vssAccessories']['valveAccessories']['0']>[];
    actuatorAccessoriesSubtotal: number;
} => {
    const comboPrice = getPriceValue(combo, userSettings.shoppingCenter as keyof typeof combo, currency);
    const valvePrice = getPriceValue(valve, userSettings.shoppingCenter as keyof typeof valve, currency);
    const actuatorPrice = actuator
        ? getPriceValue(actuator, userSettings.shoppingCenter as keyof typeof actuator, currency)
        : { currency: '', value: 0 };

    const pricePerUnit = comboPrice && comboPrice.value > 0
        ? comboPrice.value
        : (valvePrice ? valvePrice.value : 0) + (actuatorPrice ? actuatorPrice.value : 0);

    const comboQty = checkoutState.combo.qty ?? 1;
    const currentCurrency = comboPrice?.currency || valvePrice?.currency || currency || 'USD';
    const comboSubtotal = pricePerUnit * comboQty;

    const { isRecommended } = parseRecommended(valve.connectors);

    const accessories = prepareAccessories<
    AccessoriesPageData['vssAccessories']['valveAccessories']['0']
    >(
        valveAccessoriesSource,
        checkoutState.selectedAccessories,
        userSettings,
        currency,
        (a, b) => sortAccessoriesBy(isRecommended)(a.accessory, b.accessory),
    );

    const pipeConnectors = accessories.filter(({ accessory }) => isRecommended(accessory.item.Label))
        .map((connector) => ({
            ...connector,
            qty: connector.qty * (isPipeConnectorEnabled ? comboQty : 1),
        }));
    const pipeConnectorsSubtotal = subtotalAccessories(pipeConnectors);
    const valveAccessories = accessories.filter(({ accessory }) => !isRecommended(accessory.item.Label));
    const valveAccessoriesSubtotal = subtotalAccessories(valveAccessories);
    const actuatorAccessories = prepareAccessories<
    AccessoriesPageData['vssAccessories']['valveAccessories']['0']
    >(
        actuatorAccessoriesSource || [],
        checkoutState.selectedAccessories,
        userSettings,
        currency,
        (a, b) => sortAccessoriesBy(isRecommended)(a.accessory, b.accessory),
    )
        .filter(({ accessory }) => !isRecommended(accessory.item.Label))
        .filter(({ accessory }) => valveAccessories.find((
            item,
        ) => accessory.item.Label === item.accessory.item.Label) === undefined);
    const actuatorAccessoriesSubtotal = subtotalAccessories(actuatorAccessories);

    return {
        currentCurrency,
        pricePerUnit,
        total: comboSubtotal + pipeConnectorsSubtotal + valveAccessoriesSubtotal + actuatorAccessoriesSubtotal,
        comboSubtotal,
        pipeConnectors,
        pipeConnectorsSubtotal,
        valveAccessories,
        valveAccessoriesSubtotal,
        actuatorAccessories,
        actuatorAccessoriesSubtotal,
    };
};
